import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const API_BASE = () =>
  (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

async function auth() {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    Authorization: `Bearer ${session?.access_token ?? ''}`,
    'Content-Type': 'application/json',
  };
}

/* ⛔ OS TIPOS VÊM DO BANCO (Douglas 23/09/2026). Este array era a TERCEIRA
   lista de tipos do CRM — as outras duas estavam no backend. Três listas da
   mesma coisa divergem no primeiro ajuste; agora a fonte é `tarefa_tipos`,
   editável sem deploy. */
export interface TipoTarefa {
  slug: string; nome: string; grupo: string; descricao: string | null; ordem: number;
  /** Etapas do cliente em que o tipo existe; vazio = qualquer uma (lembrete). */
  etapas?: string[]; convida?: boolean;
}

/* A DEFESA DE CRÉDITO AVISA O CONSULTOR (Douglas 28/09/2026): gravada a
   tentativa, nasce a tarefa do consultor dono com o BKO e a supervisora de
   qualidade ajudando. Falhar aqui não desfaz a defesa — só avisa. */
export async function abrirTarefaDaDefesa(tentativaId: string): Promise<{ ok: boolean; erro?: string }> {
  try {
    const r = await fetch(`${API_BASE()}/v1/tarefas/defesa-credito`, {
      method: 'POST', headers: await auth(), body: JSON.stringify({ tentativa_id: tentativaId }),
    });
    const corpo = await r.json().catch(() => ({}));
    return r.ok ? { ok: true } : { ok: false, erro: corpo.error || `Erro ${r.status}` };
  } catch (e) {
    return { ok: false, erro: (e as Error).message };
  }
}

/** O nome de cada etapa do cliente, na língua do Kanban. */
export const ETAPA_NOME: Record<string, string> = {
  lead: 'Lead', proposta: 'Proposta', contrato: 'Contrato', credito: 'Crédito',
  tramitacao: 'Tramitação', pre_ativo: 'Pré-ativo', esim: 'Validação eSIM',
  boas_vindas: 'Boas-vindas', faturas: 'Faturas', carteira: 'Carteira', cancelado: 'Cancelado',
  carteira_atendimento: 'Carteira · ciclo finalizado',
};

export interface OpcoesTarefa {
  etapas: string[];
  /** Os tipos que cabem na etapa do cliente agora. */
  tipos: string[];
  /** A tarefa já aberta do cliente: enquanto existir, não se abre outra. */
  aberta: { id: string; titulo: string; responsavel_nome: string | null; categoria: string | null } | null;
  /** A tarefa vai sempre para o consultor dono do cliente; sem ele, não se abre. */
  dono?: { id: string; nome: string; cliente_id: string | null } | null;
  /** Só quando a tarefa nasce fora do Inbox: a conversa em que ela vai ficar. */
  conversa?: { id: string; telefone: string | null; conexao: string | null } | null;
  /** E o lead do cliente em que ela vai ficar (o caminho até ele sem conversa). */
  lead?: { id: string; status: string | null; tipo: string | null } | null;
}

/** Um cliente achado na busca da Nova tarefa (página Tarefas). */
export interface ClienteTarefa {
  cliente_id: string; documento: string | null; razao_social: string | null;
  dono_nome: string | null; equipe_nome: string | null;
  etapas: string[] | null; tarefa_aberta: string | null;
}

/* NOVA TAREFA PELO CNPJ (Douglas 02/10/2026): na página Tarefas o cliente se
   escolhe pela busca; o resto é o mesmo modal do Inbox. */
export function useBuscarClientesTarefa(termo: string) {
  const t = termo.trim();
  const ok = t.replace(/\D/g, '').length >= 5 || t.length >= 3;
  return useQuery({
    queryKey: ['tarefas', 'clientes', t],
    enabled: ok,
    staleTime: 15_000,
    queryFn: async (): Promise<ClienteTarefa[]> => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/clientes?q=${encodeURIComponent(t)}`, { headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível buscar o cliente.');
      return corpo.data ?? [];
    },
  });
}

/* A TAREFA TEM QUE TER A VER COM A FILA DO CLIENTE (Douglas 28/09/2026). A
   régua é do banco; a tela só mostra o que cabe. */
export function useOpcoesTarefa(alvo: { cliente_id?: string | null; lead_id?: string | null; cnpj?: string | null; conversa_id?: string | null; pedido_id?: string | null; ligar_conversa?: string | null }, enabled = true) {
  const p = new URLSearchParams();
  Object.entries(alvo).forEach(([k, v]) => { if (v) p.set(k, String(v)); });
  const chave = p.toString();
  return useQuery({
    queryKey: ['tarefas', 'opcoes', chave],
    enabled: enabled && !!chave,
    staleTime: 15_000,
    queryFn: async (): Promise<OpcoesTarefa> => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/opcoes?${chave}`, { headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível ler a etapa do cliente.');
      return corpo.data;
    },
  });
}

export function useTiposTarefa() {
  return useQuery({
    queryKey: ['tarefas', 'tipos'],
    staleTime: 300_000,
    queryFn: async (): Promise<TipoTarefa[]> => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/tipos`, { headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível carregar os tipos de tarefa.');
      return corpo.data ?? [];
    },
  });
}

/** Hora de um timestamptz que veio do banco como texto ("2026-10-02 13:35:00-03"). */
export function horaDoAviso(v: string): string {
  const d = new Date(String(v).replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export type CategoriaTarefa = string;
export type EstadoTarefa = 'vencida' | 'hoje' | 'amanha' | 'futura' | 'em_andamento' | 'cumprida' | 'cancelada';

export interface Tarefa {
  id: string;
  titulo: string;
  descricao: string | null;
  vence_em: string;
  estado: EstadoTarefa;
  categoria: CategoriaTarefa;
  tipo_nome: string;
  tipo_grupo: string;
  responsavel_equipe_nome: string | null;
  adiada_vezes: number;
  concluida_em: string | null;
  conclusao_nota: string | null;
  cliente_nome: string | null;
  cnpj: string | null;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  criado_por_nome: string | null;
  conversa_id: string | null;
  cliente_id: string | null;
  lead_id: string | null;
  pedido_id: string | null;
  /** A fila do Kanban do cliente AGORA — lida no banco a cada consulta, nunca gravada. */
  fila_quadro?: string | null;
  fila_coluna?: string | null;
  fila_mensagem?: string | null;
  fila_ciclo?: string | null;
  /** Todas as filas vivas do cliente e as etapas do ciclo da Qualidade (fn_tarefa_kanban). */
  kanban?: KanbanDaTarefa | null;
  /** O lead ligado à tarefa, quando o cliente ainda não tem pedido. */
  lead_status?: string | null;
  lead_tipo?: string | null;
  convidados?: ConvidadoTarefa[];
  ajuda_pendente?: number;
  ajuda_total?: number;
  status_trabalho?: StatusTrabalho;
  /** Só em "Pediram minha ajuda": o que eu respondi. */
  minha_resposta?: string | null;
  meu_pedido_em?: string | null;
  minha_resposta_em?: string | null;
  pedido_por_nome?: string | null;
  supervisor_nome?: string | null;
  conversa_telefone?: string | null;
  /** A IsabellA registrou esta tarefa em nome do dono (origem IA_*). */
  aberta_pela_ia?: boolean;
  /** Quem abriu: a IsabellA ou a Conecta IA (o retorno combinado da venda CPF). */
  ia_nome?: string | null;
  /** Atendimento ao dono: o WhatsApp do cliente, quem abriu e o que ele precisa. */
  whatsapp_cliente?: string | null;
  aberto_por_nome?: string | null;
  /** Tratar depois no aviso: quando o aviso volta a abrir (só enquanto no futuro). */
  aviso_volta_em?: string | null;
  cliente_precisa?: string | null;
  /** Convite de ajuda que veio de um Atendimento ao dono (já tem o cartão próprio). */
  atendimento_dono?: boolean;
  /** Fui eu quem abriu a tarefa para o dono: sou ajuda, mas não preciso do aviso de convite. */
  aberta_por_mim?: boolean;
}

export type StatusTrabalho = 'em_aberto' | 'em_andamento' | 'aguardando_ajuda' | 'ajuda_respondida' | 'vencida' | 'cumprida' | 'cancelada';

export interface ConvidadoTarefa {
  usuario_id: string; nome: string; status: 'pendente' | 'respondido';
  resposta: string | null; respondido_em: string | null;
  /** Quando a ajuda foi pedida: o SLA de 2 h conta daqui. */
  pedido_em?: string | null;
}

/** `obrigatorio`: quem ajuda por regra do caso — entra sozinho na tarefa. */
export interface Convidavel { usuario_id: string; nome: string; papel: string; obrigatorio?: boolean }

/** Onde o cliente está no Kanban agora — a mesma leitura da página Tarefas. */
export interface FilaCliente { quadro: string | null; coluna: string | null; mensagem: string | null; ciclo: string | null }

const CICLO_NOME: Record<string, string> = { ativacao_esim: 'Verificação do eSIM', boas_vindas: 'Boas-vindas', fatura_1: 'Primeira fatura' };
const QUADRO_NOME: Record<string, string> = {
  COMERCIAL: 'Comercial', CONTRATO_PRIME: 'Contrato', TRAMITACAO_PRIME: 'Tramitação Prime',
  TRAMITACAO_VIVO: 'Tramitação operadora', PRE_ATIVO: 'Pré-ativo', QUALIDADE: 'Qualidade',
};

export interface KanbanDaTarefa {
  filas: Array<{ quadro: string; coluna: string | null; produtos: string | null }>;
  ciclo: Array<{ etapa: string; status: string; em: string | null; nota: number | null }>;
}

const CICLO_CURTO: Record<string, string> = { ativacao_esim: 'eSIM', boas_vindas: 'boas-vindas', fatura_1: '1ª fatura' };

function legivel(v: string) {
  const t = v.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function dia(iso: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * ONDE O CLIENTE DA TAREFA ESTÁ (Douglas 11/10/2026: "nas tarefas não está mais
 * aparecendo qual é o status que ele está do Kanban... isso tem que ter").
 * A lista e o modal leem daqui: a fila do Kanban agora (quadro · coluna), as
 * outras filas vivas do cliente e o ciclo da Qualidade, aberto ou concluído.
 */
export function filaDaTarefa(t: Tarefa) {
  const ciclo = t.kanban?.ciclo ?? [];
  const pendente = t.fila_ciclo || ciclo.find((c) => c.status === 'pendente')?.etapa || null;
  const cicloFechado = !pendente && ciclo.length > 0;
  const quadro = t.fila_quadro ? QUADRO_NOME[t.fila_quadro] || t.fila_quadro : null;
  const agora = quadro ? `${quadro}${t.fila_coluna ? ` · ${t.fila_coluna}` : ''}` : null;
  const outras = (t.kanban?.filas ?? [])
    .filter((f) => !(f.quadro === t.fila_quadro && f.coluna === t.fila_coluna))
    .map((f) => `${QUADRO_NOME[f.quadro] || f.quadro}${f.coluna ? ` · ${f.coluna}` : ''}${f.produtos ? ` (${f.produtos})` : ''}`);
  const feitas = ciclo
    .filter((c) => c.status !== 'pendente')
    .map((c) => {
      const quando = dia(c.em);
      const nota = c.etapa === 'boas_vindas' && c.nota != null ? ` nota ${c.nota}` : '';
      return `${CICLO_CURTO[c.etapa] || c.etapa}${nota}${quando ? `${nota ? ' em' : ''} ${quando}` : ''}`;
    });
  const nomePendente = pendente ? CICLO_NOME[pendente] || pendente : null;
  const cicloTexto = nomePendente
    ? [`${nomePendente} em andamento`, ...feitas].join(' · ')
    : cicloFechado ? ['concluído', ...feitas].join(' · ') : null;
  const cicloSelo = nomePendente ? `Ciclo · ${nomePendente}` : cicloFechado ? 'Ciclo concluído' : null;

  let topo: string;
  let base: string;
  if (nomePendente) {
    topo = `Ciclo · ${nomePendente}`;
    base = agora || 'sem pedido';
  } else if (quadro) {
    topo = t.fila_coluna || quadro;
    base = cicloFechado ? `${quadro} · ciclo concluído` : quadro;
  } else if (t.lead_status) {
    /* SEM PEDIDO, A FILA É O LEAD (Douglas 02/10/2026). */
    topo = `Lead · ${legivel(t.lead_status)}`;
    base = `${t.lead_tipo ? `${legivel(t.lead_tipo)} · ` : ''}sem pedido`;
  } else {
    topo = 'Lead';
    base = 'sem pedido';
  }
  return { agora, outras, cicloTexto, cicloSelo, topo, base };
}

/** "Ciclo · Boas-vindas", "Contrato · BIOMETRIA" ou "Lead · sem pedido". */
export function rotuloFila(f: Pick<FilaCliente, 'quadro' | 'coluna' | 'ciclo'> | null | undefined) {
  if (f?.ciclo) return `Ciclo · ${CICLO_NOME[f.ciclo] || f.ciclo}`;
  if (f?.quadro) return `${QUADRO_NOME[f.quadro] || f.quadro}${f.coluna ? ` · ${f.coluna}` : ''}`;
  return 'Lead · sem pedido';
}

export interface ResumoTarefas {
  /** As abertas do recorte — o MESMO número da lista, com ou sem tipo ativo. */
  abertas?: number;
  vencidas: number;
  em_andamento: number;
  hoje: number;
  amanha: number;
  adiadas: number;
  cumpridas_mes: number;
  no_prazo_pct: number;
  dias_para_cumprir: number;
  venceram_sem_tratar: number;
  aguardando_ajuda: number;
  ajuda_respondida: number;
  ajuda: { para_responder: number; respondidas: number };
  /** A coluna da esquerda da página: grupo, tipo e quantas em aberto. */
  por_tipo: Array<{ slug: string; nome: string; grupo: string; ordem: number; fila: number }>;
}

export interface FiltrosTarefas {
  destaque_cnpj?: string;
  escopo?: 'minhas' | 'equipe' | 'todas';
  equipe_id?: string;
  responsavel_id?: string;
  categoria?: string;
  estado?: 'vencida' | 'hoje' | 'amanha' | 'abertas' | 'cumprida' | 'todas';
  busca?: string;
  adiadas?: boolean;
}

function qs(f: FiltrosTarefas) {
  const p = new URLSearchParams();
  if (f.destaque_cnpj) p.set('destaque_cnpj', f.destaque_cnpj);
  if (f.escopo) p.set('escopo', f.escopo);
  if (f.categoria) p.set('categoria', f.categoria);
  if (f.estado) p.set('estado', f.estado);
  if (f.busca?.trim()) p.set('busca', f.busca.trim());
  if (f.adiadas) p.set('adiadas', 'true');
  return p.toString();
}

/**
 * Lista de tarefas. O escopo (consultor vê as dele, supervisor pode pedir a
 * equipe, gestão vê todas) é resolvido no backend — não dá pra burlar pela tela.
 */
export function useTarefas(filtros: FiltrosTarefas) {
  return useQuery({
    queryKey: ['tarefas', filtros],
    staleTime: 20_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const res = await fetch(`${API_BASE()}/v1/tarefas?${qs(filtros)}`, { headers: await auth() });
      if (!res.ok) throw new Error(`tarefas ${res.status}`);
      const j = await res.json();
      return (j?.data ?? []) as Tarefa[];
    },
  });
}

export function useResumoTarefas(filtros: FiltrosTarefas) {
  return useQuery<ResumoTarefas>({
    /* O resumo carrega o MESMO recorte da lista — escopo, equipe e consultor.
       Tiles e coluna da esquerda contando coisa diferente da lista é mentir no
       número, e foi assim que ninguém percebeu as 93 vencidas. */
    queryKey: ['tarefas', 'resumo', filtros.escopo, filtros.equipe_id, filtros.responsavel_id],
    staleTime: 20_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const res = await fetch(`${API_BASE()}/v1/tarefas/resumo?${qs({
        escopo: filtros.escopo, equipe_id: filtros.equipe_id, responsavel_id: filtros.responsavel_id,
      })}`, { headers: await auth() });
      if (!res.ok) throw new Error(`tarefas resumo ${res.status}`);
      const d = (await res.json())?.data ?? {};
      return {
        vencidas: Number(d.vencidas ?? 0),
        em_andamento: Number(d.em_andamento ?? 0),
        hoje: Number(d.hoje ?? 0),
        amanha: Number(d.amanha ?? 0),
        adiadas: Number(d.adiadas ?? 0),
        cumpridas_mes: Number(d.cumpridas_mes ?? 0),
        no_prazo_pct: Number(d.no_prazo_pct ?? 0),
        dias_para_cumprir: Number(d.dias_para_cumprir ?? 0),
        venceram_sem_tratar: Number(d.venceram_sem_tratar ?? 0),
        aguardando_ajuda: Number(d.aguardando_ajuda ?? 0),
        ajuda_respondida: Number(d.ajuda_respondida ?? 0),
        ajuda: { para_responder: Number(d.ajuda?.para_responder ?? 0), respondidas: Number(d.ajuda?.respondidas ?? 0) },
        por_tipo: Array.isArray(d.por_tipo) ? d.por_tipo : [],
      };
    },
  });
}

export interface NovaTarefa {
  titulo: string;
  descricao?: string;
  categoria: CategoriaTarefa;
  vence_em: string;
  responsavel_id?: string;
  conversa_id?: string;
  cliente_id?: string;
  lead_id?: string;
  pedido_id?: string;
  cnpj?: string;
  /** Quem vai ajudar — a régua de quem pode é do banco, pela fila do cliente. */
  convidados?: string[];
  /** Aberta pela página Tarefas: o servidor liga à conversa do cliente. */
  ligar_conversa?: boolean;
}

/** Quem pode ser convidado: pela fila do Kanban do cliente agora. */
export function useConvidaveis(alvo: { tarefa_id?: string | null; cliente_id?: string | null; lead_id?: string | null; cnpj?: string | null; conversa_id?: string | null; categoria?: string | null }, enabled = true) {
  const p = new URLSearchParams();
  Object.entries(alvo).forEach(([k, v]) => { if (v) p.set(k, String(v)); });
  const chave = p.toString();
  return useQuery({
    queryKey: ['tarefas', 'convidaveis', chave],
    enabled: enabled && !!chave,
    staleTime: 60_000,
    queryFn: async (): Promise<{ convidaveis: Convidavel[]; fila: FilaCliente | null }> => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/convidaveis?${chave}`, { headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível carregar quem pode ajudar.');
      return { convidaveis: corpo.data ?? [], fila: corpo.fila ?? null };
    },
  });
}

/** Pediram minha ajuda. */
export function useTarefasAjuda(estado: 'pendente' | 'respondido', enabled = true) {
  return useQuery({
    queryKey: ['tarefas', 'ajuda', estado],
    enabled,
    staleTime: 20_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Tarefa[]> => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/ajuda?estado=${estado}`, { headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível carregar os pedidos de ajuda.');
      return corpo.data ?? [];
    },
  });
}

export function useResponderAjuda() {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: async ({ id, resposta }: { id: string; resposta: string }) => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/${id}/responder`, {
        method: 'POST', headers: await auth(), body: JSON.stringify({ resposta }),
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível enviar a resposta.');
      return corpo;
    },
    onSuccess: invalidar,
  });
}

export function useConvidarTarefa() {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: async ({ id, usuario_ids }: { id: string; usuario_ids: string[] }) => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/${id}/convidados`, {
        method: 'POST', headers: await auth(), body: JSON.stringify({ usuario_ids }),
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível convidar.');
      return corpo;
    },
    onSuccess: invalidar,
  });
}

/** Invalida tarefa, timeline e avisos — a criação mexe nos três. */
function useInvalidar() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['tarefas'] });
    void qc.invalidateQueries({ queryKey: ['timeline-unificada'] });
  };
}

export function useCriarTarefa() {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: async (t: NovaTarefa) => {
      const res = await fetch(`${API_BASE()}/v1/tarefas`, {
        method: 'POST', headers: await auth(), body: JSON.stringify(t),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || `Não deu pra agendar (${res.status})`);
      return j?.data as { id: string; cnpj: string | null; conversa_id?: string | null };
    },
    onSuccess: invalidar,
  });
}

/** O MEIO: comecei a tarefa e ela para de contar como dívida enquanto ando. */
export function useIniciarTarefa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/${id}/iniciar`, { method: 'POST', headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível iniciar a tarefa.');
      return corpo.data;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tarefas'] }); },
  });
}

export function useCumprirTarefa() {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: async ({ id, nota }: { id: string; nota: string }) => {
      const res = await fetch(`${API_BASE()}/v1/tarefas/${id}/cumprir`, {
        method: 'PATCH', headers: await auth(), body: JSON.stringify({ nota }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || `Não deu pra fechar (${res.status})`);
      return j;
    },
    onSuccess: invalidar,
  });
}

export function useAdiarTarefa() {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: async ({ id, vence_em, motivo }: { id: string; vence_em: string; motivo: string }) => {
      const res = await fetch(`${API_BASE()}/v1/tarefas/${id}/adiar`, {
        method: 'PATCH', headers: await auth(), body: JSON.stringify({ vence_em, motivo }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || `Não deu pra adiar (${res.status})`);
      return j;
    },
    onSuccess: invalidar,
  });
}

/** Uma tarefa aberta minha, ou um convite pendente, para marcar a conversa. */
export interface MarcaTarefa {
  id: string; conversa_id: string | null; cnpj: string | null; titulo: string; tipo_nome: string;
  vence_em: string; estado: EstadoTarefa; responsavel_nome: string | null; papel: 'minha' | 'ajuda';
  convidados: Array<{ nome: string; status: 'pendente' | 'respondido'; resposta: string | null }> | null;
  aberta_pela_ia?: boolean;
  /** Quem abriu: a IsabellA ou a Conecta IA (o retorno combinado da venda CPF). */
  ia_nome?: string | null;
  /** A nota interna que a tarefa deixou na conversa — é nela que mora o Finalizar. */
  nota_id?: string | null;
  /** Tratar depois no aviso: quando o aviso volta a abrir (só enquanto no futuro). */
  aviso_volta_em?: string | null;
}

/**
 * MARCAS DO INBOX (Douglas 23/09/2026) — UMA consulta com as minhas tarefas
 * abertas e os meus convites pendentes. A lista e o Painel casam por conversa
 * (ou CNPJ) na tela; a consulta da lista do Inbox não muda.
 */
export function useMarcasTarefa() {
  const q = useQuery({
    queryKey: ['tarefas', 'marcas'],
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<MarcaTarefa[]> => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/marcas`, { headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível carregar as tarefas.');
      return corpo.data ?? [];
    },
  });
  const porConversa = new Map<string, MarcaTarefa[]>();
  const porCnpj = new Map<string, MarcaTarefa[]>();
  for (const m of q.data ?? []) {
    if (m.conversa_id) porConversa.set(m.conversa_id, [...(porConversa.get(m.conversa_id) ?? []), m]);
    const c = (m.cnpj || '').replace(/\D/g, '');
    if (c) porCnpj.set(c, [...(porCnpj.get(c) ?? []), m]);
  }
  const daConversa = (conversaId?: string | null, cnpj?: string | null): MarcaTarefa[] => {
    const c = (cnpj || '').replace(/\D/g, '');
    const lista = [...(conversaId ? porConversa.get(conversaId) ?? [] : []), ...(c ? porCnpj.get(c) ?? [] : [])];
    return lista.filter((m, i) => lista.findIndex((x) => x.id === m.id && x.papel === m.papel) === i);
  };
  return { ...q, daConversa };
}

/** O selo que a conversa ganha: vencida > hoje > ajuda > futura. */
export function seloTarefa(marcas: MarcaTarefa[]): { texto: string; forte: boolean; titulo: string } | null {
  if (!marcas.length) return null;
  const minhas = marcas.filter((m) => m.papel === 'minha');
  const titulo = marcas.map((m) => `${m.papel === 'ajuda' ? `Ajuda para ${m.responsavel_nome}` : m.tipo_nome}: ${m.titulo}`).join('\n');
  if (minhas.some((m) => m.estado === 'vencida')) return { texto: 'Tarefa vencida', forte: true, titulo };
  if (minhas.some((m) => m.estado === 'hoje')) return { texto: 'Tarefa hoje', forte: false, titulo };
  if (marcas.some((m) => m.papel === 'ajuda')) return { texto: 'Pediram ajuda', forte: false, titulo };
  const prox = minhas[0];
  const d = new Date(prox.vence_em);
  return { texto: `Tarefa ${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`, forte: false, titulo };
}

/* ABRIR ATENDIMENTO AO DONO (Douglas 30/09/2026): qualquer pessoa, pelo Busca
   360, pede que o dono do cliente atenda. O dono e o supervisor vêm do banco. */
export interface DestinoAtendimento {
  cliente_id: string; cnpj: string | null; empresa: string | null;
  dono_id: string; dono_nome: string; equipe_nome: string | null; nivel: string | null;
  supervisor_id: string | null; supervisor_nome: string | null;
  aberta: { id: string; titulo: string; responsavel_nome: string | null } | null;
}

export function useDestinoAtendimento(clienteId: string | null, habilitado: boolean) {
  return useQuery({
    queryKey: ['tarefas', 'atendimento-dono', clienteId],
    enabled: habilitado && !!clienteId,
    staleTime: 15_000,
    queryFn: async (): Promise<DestinoAtendimento> => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/atendimento-dono/destino?cliente_id=${clienteId}`, { headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível achar o dono deste cliente.');
      return corpo.data as DestinoAtendimento;
    },
  });
}

export function useAbrirAtendimentoDono() {
  const invalidar = useInvalidar();
  return useMutation({
    mutationFn: async (b: { cliente_id: string; whatsapp: string; precisa: string; fazer: string }) => {
      const r = await fetch(`${API_BASE()}/v1/tarefas/atendimento-dono`, {
        method: 'POST', headers: await auth(), body: JSON.stringify(b),
      });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || `Não deu para abrir (${r.status})`);
      return corpo.data as { tarefa_id: string; nova: boolean; dono_nome: string };
    },
    onSuccess: invalidar,
  });
}

/** Abre a conversa com o cliente da tarefa pela linha do dono. */
export async function chamarClienteDaTarefa(tarefaId: string): Promise<string> {
  const r = await fetch(`${API_BASE()}/v1/tarefas/${tarefaId}/chamar-whatsapp`, { method: 'POST', headers: await auth() });
  const corpo = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(corpo.error || 'Não foi possível abrir a conversa.');
  return corpo.data.conversation_id as string;
}

/* PAINEL DO TOPO (Douglas 30/09/2026): quem abre tarefa, para quem, e quem não
   finaliza — no lugar da Nota Prime. Mesmo escopo por cargo da página Tarefas. */
export interface PainelTarefas {
  abertas: number; feitas: number; vencidas: number; pela_ia: number;
  quem_abre: Array<{ nome: string | null; cargo: string | null; ia: boolean; usuario_id: string | null; abertas: number; feitas: number; vencidas: number }>;
  quem_nao_finaliza: Array<{ nome: string; usuario_id: string; recebeu: number; pela_ia: number; feitas: number; vencidas: number }>;
}

export function useTarefasPainel(dias: number, equipeId: string | null, habilitado = true) {
  return useQuery({
    queryKey: ['tarefas', 'painel', dias, equipeId],
    enabled: habilitado,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    queryFn: async (): Promise<PainelTarefas> => {
      const p = new URLSearchParams({ dias: String(dias) });
      if (equipeId) p.set('equipe_id', equipeId);
      const r = await fetch(`${API_BASE()}/v1/tarefas/painel?${p}`, { headers: await auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.error || 'Não foi possível carregar o painel de tarefas.');
      return corpo.data as PainelTarefas;
    },
  });
}

/** As equipes para o filtro do painel do topo (só quem vê a casa toda filtra). */
export function useEquipesParaFiltro(habilitado: boolean) {
  return useQuery({
    queryKey: ['tarefas', 'painel', 'equipes'],
    enabled: habilitado,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('equipes').select('id, nome').order('nome');
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; nome: string }>;
    },
  });
}
